import { useCallback, useEffect, useState } from "react";

export type TocMeeting = {
  id: number;
  startDatetime: string | null | undefined;
  meetingGroupId: number;
};

// Shared DOM id for each meeting row so the TOC line buttons can scroll the
// matching row into view via document.getElementById.
export const meetingRowDomId = (meetingId: number) =>
  `meeting-row-${meetingId}`;

// Watches the Layout.Main scroll container and returns the id of the displayed
// meeting whose top is currently at the top of the visible area. The TOC uses
// this to highlight which meeting line corresponds to what the user is
// actively reading.
//
// Click handlers call `setActiveMeetingId(id)` immediately — this stores the
// value as "pending" and gives it precedence over the scroll-derived id.
// Pending is cleared when the scroll listener observes the row landing at the
// top of the viewport (i.e. the smooth-scroll has caught up), or after a
// safety timeout so manual scrolling can eventually take over. This handles
// the case where the user clicks a meeting that hasn't loaded yet: the line
// stays selected through the GraphQL fetch + smooth scroll, instead of
// flipping back to whichever loaded meeting happens to be at the top.
export const useActiveMeetingId = (
  displayedIds: number[],
): {
  activeMeetingId: number | null;
  setActiveMeetingId: (id: number | null) => void;
} => {
  const [scrollActiveId, setScrollActiveId] = useState<number | null>(null);
  const [pendingActiveId, setPendingActiveId] = useState<number | null>(null);

  useEffect(() => {
    if (displayedIds.length === 0) return;
    // Walk up from a known meeting row to the actual scrollable ancestor.
    // The DOM contains two <main> elements (the global app shell + Layout.Main),
    // and only Layout.Main is the scroll container — so `querySelector("main")`
    // grabs the wrong one. Finding the closest scrollable ancestor of a row
    // works whether we're inside the meeting page's Layout.Main or the profile
    // tab's scroll container.
    const findScroller = (): HTMLElement | null => {
      for (const id of displayedIds) {
        const row = document.getElementById(meetingRowDomId(id));
        if (!row) continue;
        let parent = row.parentElement;
        while (parent) {
          const overflowY = window.getComputedStyle(parent).overflowY;
          if (overflowY === "scroll" || overflowY === "auto") return parent;
          parent = parent.parentElement;
        }
      }
      return null;
    };
    const scroller = findScroller();
    if (!scroller) return;

    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const rect = scroller.getBoundingClientRect();
      // A row is "active" when its top has scrolled past this line (a little
      // below the top of the scroll container, to avoid flickering when the
      // header area is in view).
      const threshold = rect.top + 80;

      let bestId: number | null = null;
      let bestTop = -Infinity;
      for (const id of displayedIds) {
        const el = document.getElementById(meetingRowDomId(id));
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= threshold && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }
      // If we haven't scrolled into any row yet (everything is below the
      // threshold), default to the topmost displayed meeting.
      setScrollActiveId(bestId ?? displayedIds[0]);
    };

    const onScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    // Run once after layout settles so we pick up the initial active meeting.
    const initial = window.setTimeout(update, 50);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.clearTimeout(initial);
    };
  }, [displayedIds]);

  // Clear pending the moment the scroll listener confirms the row has landed
  // at the top of the viewport (the common, happy-path case).
  useEffect(() => {
    if (pendingActiveId !== null && scrollActiveId === pendingActiveId) {
      setPendingActiveId(null);
    }
  }, [pendingActiveId, scrollActiveId]);

  // Safety net: clear pending 3s after it was set, so manual scrolling can
  // take over even if the scroll never lands exactly on the pending row.
  // One-shot — does NOT reset on scroll updates, so the scroll-driven
  // highlight starts following the user's scroll position again after at
  // most 3 seconds.
  useEffect(() => {
    if (pendingActiveId === null) return;
    const timeout = window.setTimeout(() => setPendingActiveId(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [pendingActiveId]);

  const setActiveMeetingId = useCallback((id: number | null) => {
    setPendingActiveId(id);
  }, []);

  return {
    activeMeetingId: pendingActiveId ?? scrollActiveId,
    setActiveMeetingId,
  };
};
